import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewChecked,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  forwardRef,
  Provider,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, UntypedFormControl } from '@angular/forms';
import { UtilService } from '../../shared/util/util.service';
import {
  ObjectValueAccessor,
  CreateObjectValueAccessorProvider,
  CreateObjectValueValidationProvider,
} from '../../shared/widgets/value-accessors/object-value-accessor';
import { CreditCardPaymentInfo } from '../shared/credit-card/credit-card-payment-info.model';
import { CreditCardService } from '../shared/credit-card/credit-card.service';
import { IValidationComponent } from '../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../shared/translation/translation.service';
import { CardConnectInputComponent } from '../../integrations/card-connect/card-connect-input/card-connect-input.component';
import { PrecznService } from '../../integrations/preczn/shared/preczn.service';
import { ProfileAddress } from '../../profiles/shared/profileAddress.model';
import { TokenizeGatewayDto } from '../shared/gateway-resolution/dto/tokenize-gateway.dto';

@Component({
  selector: 'wl-credit-card-input',
  templateUrl: 'credit-card-input.component.html',
  providers: [
    CreditCardService,

    CreateObjectValueAccessorProvider(CreditCardInputComponent),
    CreateObjectValueValidationProvider(CreditCardInputComponent),
  ],
  standalone: false
})
export class CreditCardInputComponent extends ObjectValueAccessor<CreditCardPaymentInfo>
  implements OnInit, OnChanges, IValidationComponent {
  //for validation
  @Input() required: any;
  @Input() fieldCellCssClass: string = 'col-sm-9';
  @Input() defaultBillingAddress: ProfileAddress;
  // When true, no card is collected on this path (e.g. a non-credit-card payment type in the
  // Create Unapplied Payment dialog) - the NG_VALIDATORS path returns valid without consulting any
  // card field. Defaults to false so every existing consumer that does not bind it is unaffected by
  // construction (R1.3). isValid()/getValidationResult() are deliberately NOT short-circuited by
  // this flag - see the overridden validate() below.
  @Input() cardNotApplicable: boolean = false;
  // The gateway this widget must tokenize against, supplied whole or as null by the host (never a
  // partial pair). Null means "no Preczn gateway is named for this surface" - the widget does not
  // retarget the SDK and the pre-existing tenant-wide behaviour stands.
  @Input() resolvedGateway: TokenizeGatewayDto | null = null;
  @ViewChild('cardConnectInput', { static: false }) cardConnectInputComponent: CardConnectInputComponent;
  @ViewChild('precznCardNumber', { static: false }) precznCardNumberInput: ElementRef<HTMLInputElement>;
  @ViewChild('precznCvv', { static: false }) precznCvvInput: ElementRef<HTMLInputElement>;
  @Output() onCreditCardNumberChanged = new EventEmitter<CreditCardPaymentInfo>();

  public _translationService: TranslationService;
  public _utilService: UtilService;
  public _creditcardService: CreditCardService;

  public expireMonths: Array<number>;
  public expireYears: Array<number>;
  public currentMonth: number;
  public currentYear: number;
  public useCardConnect: boolean;
  public usePreczn: boolean = false;
  public precznConfigError: boolean = false;

  public cardNumberTouched: boolean = false;

  public get showCardNumberInvalid(): boolean {
    if (this.useCardConnect || this.usePreczn) return false;
    if (!this.cardNumberTouched) return false;
    return this._creditcardService.isValid(this.value.CardNum) === false;
  }

  get precznExpirationValue(): string {
    if (!this.value?.ValidThruMonth || !this.value?.ValidThruYear) return '';
    const month = ('0' + this.value.ValidThruMonth).slice(-2);
    const year = this.value.ValidThruYear.toString().slice(-2);
    return month + year;
  }

  public constructor(translationService: TranslationService, utilService: UtilService, creditcardService: CreditCardService, private _precznService: PrecznService, private _cdr: ChangeDetectorRef) {
    super(new CreditCardPaymentInfo(), false);

    this._translationService = translationService;
    this._utilService = utilService;
    this._creditcardService = creditcardService;

    this.expireMonths = new Array<number>();
    this.expireYears = new Array<number>();

    let currentDate = new Date();
    this.currentMonth = currentDate.getMonth() + 1;
    this.currentYear = currentDate.getFullYear();

    this.expireMonths.push(1);
    this.expireMonths.push(2);
    this.expireMonths.push(3);
    this.expireMonths.push(4);
    this.expireMonths.push(5);
    this.expireMonths.push(6);
    this.expireMonths.push(7);
    this.expireMonths.push(8);
    this.expireMonths.push(9);
    this.expireMonths.push(10);
    this.expireMonths.push(11);
    this.expireMonths.push(12);

    this.expireYears.push(this.currentYear);
    this.expireYears.push(this.currentYear + 1);
    this.expireYears.push(this.currentYear + 2);
    this.expireYears.push(this.currentYear + 3);
    this.expireYears.push(this.currentYear + 4);
    this.expireYears.push(this.currentYear + 5);
    this.expireYears.push(this.currentYear + 6);
    this.expireYears.push(this.currentYear + 7);
    this.expireYears.push(this.currentYear + 8);
    this.expireYears.push(this.currentYear + 9);
    this.expireYears.push(this.currentYear + 10);
    this.expireYears.push(this.currentYear + 11);
    this.expireYears.push(this.currentYear + 12);
    this.expireYears.push(this.currentYear + 13);
    this.expireYears.push(this.currentYear + 14);
    this.expireYears.push(this.currentYear + 15);
    this.expireYears.push(this.currentYear + 16);
    this.expireYears.push(this.currentYear + 17);
    this.expireYears.push(this.currentYear + 18);
    this.expireYears.push(this.currentYear + 19);
    this.expireYears.push(this.currentYear + 20);

    this.useCardConnect = false;
  }

  // Delegates to getValidationResult() so there is a single source of truth for validity - this used
  // to be a rubber stamp that only ever checked precznConfigError and otherwise always returned true,
  // silently ignoring blank/invalid card number, CVV, expiration, etc. Consumers that call isValid()
  // directly (e.g. credit-card-charge.component.ts's Pay button gate, and every consumer's explicit
  // precznConfigError guard added across fix rounds 1-3) now get real field-level validation too, not
  // just the config-error case. This also affects ObjectValueAccessor.validate() (NG_VALIDATORS), which
  // calls isValid() - not getValidationResult() - so [(ngModel)] consumers now get real validation via
  // ngForm.valid as well.
  public isValid = (): boolean => {
    return this.getValidationResult().isValid;
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();

    if (this.precznConfigError) {
      valid.isValid = false;
      valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.preczn-config-error'));
      return valid;
    }

    if (!this.usePreczn) {
      if (this._utilService.isNullEmptyOrWhitespace(this.value.CardNum)) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.credit-card-number-not-valid-validation-message'));
      } else if (this._creditcardService.isValid(this.value.CardNum) == false) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.credit-card-number-not-valid-validation-message'));
      } else if (this._utilService.isNullEmptyOrWhitespace(this.value.SecurityCode)) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.security-code-not-valid-validation-message'));
      }
    } else {
      // preczn hosts the card number/cvv inputs itself (data-preczn attributes, no ngModel binding),
      // so they can't be checked via this.value - read the DOM inputs directly instead.
      if (this._utilService.isNullEmptyOrWhitespace(this.precznCardNumberInput?.nativeElement.value)) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.credit-card-number-not-valid-validation-message'));
      }
      if (this._utilService.isNullEmptyOrWhitespace(this.precznCvvInput?.nativeElement.value)) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.security-code-not-valid-validation-message'));
      }
    }

    if (this.useCardConnect) {
      if (this.cardConnectInputComponent.isValid() == false) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstant('revenue.credit-card-input.security-code-not-valid-validation-message'));
      }
    }

    return valid;
  };

  // NG_VALIDATORS seam - ObjectValueAccessor.validate is an instance arrow property
  // (object-value-accessor.ts:71-81), not a prototype method, so it cannot be reached via
  // `super.validate`; this override reproduces its two-branch body verbatim for the applicable
  // case, with the cardNotApplicable carve-out added ahead of it. Mirrors the portal's freeMode
  // carve-out (Atlas.Spa.Portal/.../credit-card-input.component.ts:278-292). isValid() and
  // getValidationResult() are untouched, so ATLAS-5461's delegation and every direct isValid()/
  // getValidationResult() caller (credit-card-charge.component.ts's Pay gate, both events wizards)
  // keep reporting real card validity regardless of this flag.
  public validate = (c: UntypedFormControl): any => {
    if (this.cardNotApplicable) return null; // no card is collected on this path - see field comment above
    if (this.isValid() == false) {
      let validationResult: ValidationResult = new ValidationResult();
      validationResult.errors.push(this.validationError);
      return validationResult;
    } else {
      return null;
    }
  };

  private _onValidatorChange: () => void = () => {};
  public registerOnValidatorChange(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  ngOnInit() {
    let obj = this.value;

    this._precznService
      .isPrecznOnly()
      .then((isPrecznOnly) => {
        if (isPrecznOnly || this.resolvedGateway?.precznMerchantId) {
          this.usePreczn = true;
          // Inputs are set before ngOnInit, so a host that already knows its gateway at mount time
          // loads the SDK against it directly; one that resolves later retargets through
          // ngOnChanges -> reloadForGateway below.
          this._precznService.loadScript(this.resolvedGateway).catch(() => {
            // Do NOT reset usePreczn to false here. Every parent consumer that gates its own
            // tokenization call on `if (this.ccPaymentComponent.usePreczn) { await prepareForSubmit(); }`
            // (profile-edit-billing, pledge-edit-billing, invoice-payment-header/multi-invoice-payment,
            // create-unapplied-payment-dialog) relies on usePreczn staying true so that gate actually
            // executes and prepareForSubmit()'s own precznConfigError rejection can fire and propagate.
            // Resetting it to false here previously caused every one of those call sites to silently
            // skip prepareForSubmit() entirely.
            this.precznConfigError = true;
            this.onChange(this.value); // force NgModel/NG_VALIDATORS to re-run isValid()
          });
        }
      })
      .catch(() => {
        // Same reasoning as the loadScript() catch above: every consumer's
        // `if (this.ccPaymentComponent.usePreczn) { await prepareForSubmit(); }` gate must actually be
        // entered so prepareForSubmit()'s own precznConfigError rejection can fire. prepareForSubmit()
        // checks precznConfigError before it ever checks usePreczn or attempts to tokenize, so setting
        // usePreczn = true here does not risk a real tokenize attempt - it only unblocks the gate so the
        // clean rejection is reachable.
        this.usePreczn = true;
        this.precznConfigError = true;
        this.onChange(this.value);
      });
  }

  ngAfterViewChecked() {
    this.tokenizeCardNumIfNecessary();
  }

  ngOnChanges(changes: SimpleChanges) {
    // R1.4: flipping cardNotApplicable changes neither the control's value nor anything Angular
    // watches on its own - writeValue() discards the null the dialog pushes back (allowNull ===
    // false) without calling onChange - so validate() would otherwise keep reporting a stale
    // verdict in both directions. Force Angular to re-run NG_VALIDATORS.
    if (changes.cardNotApplicable) this._onValidatorChange();

    this.remountForResolvedGatewayChange(changes);

    // R1's ngOnChanges hazard: this is the first input the dialog binds on this widget, so this is
    // the first time ngOnChanges fires for it at all. Below this line, emitOutputObject() pushes
    // this.value (a blank CreditCardPaymentInfo) up into the consumer's model via onChange - which
    // would overwrite the dialog's deliberate `creditCardPayment = null` for a non-card payment
    // type and re-arm Save's `if (this.creditCardPayment)` branch, cancelling the fix. Skip it when
    // cardNotApplicable is the only changed key; behave exactly as before (including the
    // defaultBillingAddress branch) whenever any other input also changed.
    //
    // resolvedGateway joins cardNotApplicable in that guard for exactly the same reason: it is a
    // newly bound input on the same widget, several of whose consumers deliberately hold a null
    // card model, and a gateway-only change must not push a blank CreditCardPaymentInfo up into
    // them. The remount above runs before the guard because it is what the input exists for.
    if (Object.keys(changes).every((k) => k === 'cardNotApplicable' || k === 'resolvedGateway')) return;

    if (changes.defaultBillingAddress && this.defaultBillingAddress) {
      this.autoPopulateDefaultBillingAddress(this.defaultBillingAddress);
    }
    this.emitOutputObject();
  }

  /**
   * Retargets the Preczn SDK when the host names a different gateway after this widget mounted.
   *
   * The three unconditionally mounted Backoffice consumers (credit-card-charge,
   * invoice-payment-header, create-unapplied-payment-dialog) render this widget before any payment
   * type has been picked, so the gateway is NOT known before the SDK first loads there; this is
   * the path that corrects it. The widget's own `usePreczn = false; setTimeout(... = true)` reset
   * in clearForm() is a different mechanism - it destroys the DOM fields but does not reload the
   * script - and the two compose.
   */
  private remountForResolvedGatewayChange(changes: SimpleChanges): void {
    const gatewayChange = changes['resolvedGateway'];
    const gateway: TokenizeGatewayDto | null = gatewayChange?.currentValue ?? null;

    if (!gatewayChange || gatewayChange.firstChange || !gateway?.precznMerchantId) return;
    if (gateway.ccGatewayId === this._precznService.currentCcGatewayId) {
      // SDK is already loaded for this gateway (currentCcGatewayId is set only in script.onload).
      // No reload needed, but a freshly mounted widget on a non-isPrecznOnly tenant starts with
      // usePreczn = false — flip it so the tokenization form actually renders.
      if (!this.usePreczn) {
        this.usePreczn = true;
        this.precznConfigError = false;
        this._onValidatorChange();
        this.safeDetectChanges();
      }
      return;
    }

    void Promise.resolve().then(async () => {
      this.usePreczn = false;
      this.value.GatewayTokens = [];
      this.safeDetectChanges();
      try {
        await this._precznService.reloadForGateway(gateway);
        this.usePreczn = true;
        this.precznConfigError = false;
      } catch {
        // Same reasoning as ngOnInit's catch handlers: usePreczn must stay true so every
        // consumer's `if (usePreczn) { await prepareForSubmit(); }` gate is entered and
        // prepareForSubmit()'s own precznConfigError rejection can fire.
        this.usePreczn = true;
        this.precznConfigError = true;
        this.onChange(this.value);
      }
      this._onValidatorChange();
      this.safeDetectChanges();
    });
  }

  private safeDetectChanges(): void {
    try {
      this._cdr.detectChanges();
    } catch {
      // The view can be destroyed between the microtask being queued and it running (the widget
      // sits behind *ngIf at four of its seven consumers). Nothing to update in that case.
    }
  }

  private autoPopulateDefaultBillingAddress(address: ProfileAddress): void {
    if (!address) return;

    this.value.CreditCardContactInfo.Address1 = address.Address1;
    this.value.CreditCardContactInfo.Address2 = address.Address2;
    this.value.CreditCardContactInfo.City = address.City;
    this.value.CreditCardContactInfo.State = address.State;
    this.value.CreditCardContactInfo.Zip = address.Zip;
    this.value.CreditCardContactInfo.Country = address.Country;
  }

  modelChange() {
    this.emitOutputObject();
  }

  public onFieldChange = (): void => {
    this.onChange(this.value);
  };

  public updateCreditCardType = () => {
    this.value.CardType = this._creditcardService.getCardType(this.value.CardNum);
  };

  emitOutputObject = (): void => {
    this.updateCreditCardType();
    this.tokenizeCardNumIfNecessary();

    this.onCreditCardNumberChanged.emit(this.value);
    this.onChange(this.value);
  };

  public validThruMonthChange = (data: string) => {
    this.value.ValidThruMonth = parseInt(data);
    this.onChange(this.value);
  };
  public validThruYearChange = (data: string) => {
    this.value.ValidThruYear = parseInt(data);
    this.onChange(this.value);
  };

  /**
   * when the name on the card changes
   * @param nameOnCard
   */
  public handleNameOnCardChange(nameOnCard: string) {
    // update first/last name
    if (!nameOnCard || !this.value) return;

    let parts: Array<string> = nameOnCard.split(' ', 2);

    this.value.CreditCardContactInfo.FirstName = parts && parts.length ? parts[0] : '';
    this.value.CreditCardContactInfo.LastName = parts && parts.length > 1 ? parts[1] : '';
    this.onChange(this.value);
  }

  public getCreditCardPaymentInfo = (): CreditCardPaymentInfo => {
    this.emitOutputObject();
    return this.value;
  };

  public clearForm = (): void => {
    this.value.Amount = 0;
    this.value.CardNum = '';
    this.cardNumberTouched = false;
    this.value.CreditCardContactInfo.Address1 = '';
    this.value.CreditCardContactInfo.Address2 = '';
    this.value.CreditCardContactInfo.City = '';
    this.value.CreditCardContactInfo.State = '';
    this.value.CreditCardContactInfo.Country = '';
    this.value.CreditCardContactInfo.NameOnCard = '';
    this.value.CreditCardContactInfo.Zip = '';
    this.value.SecurityCode = '';
    this.value.ValidThruMonth = this.currentMonth;
    this.value.ValidThruYear = this.currentYear;
    this.value.GatewayTokens = [];
    // Preczn SDK owns the DOM state; toggling usePreczn destroys/recreates the inputs to reset it.
    // NOTE: this is a narrow, known exception to the "precznConfigError === true implies
    // usePreczn === true" invariant ngOnInit() now maintains (see ngOnInit()'s two catch handlers):
    // for the single macrotask between this synchronous `usePreczn = false` and the setTimeout()
    // callback restoring it, usePreczn is momentarily false even if precznConfigError is true. No
    // user-initiated save can land in that window, but any consumer relying solely on the invariant
    // (rather than checking precznConfigError/isValid() directly) should be aware of it.
    if (this.usePreczn) {
      this.usePreczn = false;
      setTimeout(() => { this.usePreczn = true; }, 0);
    }
    this.cardConnectInputComponent?.clearForm();
  };

  /**
   * Called by InvoicePaymentHeaderComponent when the user selects a Preczn-backed payment
   * type on a multi-gateway tenant (isPrecznOnly() returned false so ngOnInit did not activate
   * Preczn mode). Sets usePreczn immediately so the Preczn form renders, then loads the SDK.
   */
  public enablePrecznMode(): void {
    if (this.usePreczn) return;
    this.usePreczn = true;
    this._precznService.loadScript().catch(() => {
      // Mirror the ngOnInit loadScript catch: keep usePreczn = true so prepareForSubmit()'s
      // precznConfigError rejection path is reachable by the parent caller.
      this.precznConfigError = true;
      this.onChange(this.value);
    });
  }

  /**
   * Called by InvoicePaymentHeaderComponent when the user switches away from a Preczn-backed
   * payment type to a legacy gateway. Restores the legacy card form.
   */
  public disablePrecznMode(): void {
    this.usePreczn = false;
    this.precznConfigError = false;
    this.value.GatewayTokens = [];
    this.onChange(this.value);
  }

  public async prepareForSubmit(): Promise<void> {
    if (this.precznConfigError) {
      return Promise.reject(new Error(this._translationService.getInstant('revenue.credit-card-input.preczn-config-error')));
    }
    if (!this.usePreczn) { return; }
    const token = await this._precznService.getToken();
    // currentCcGatewayId is set in the same script.onload handler that built the SDK URL, and
    // loadScript()'s promise has resolved by the time getToken() can succeed, so the tag names the
    // gateway that actually minted this token rather than a guess about which one did.
    this.value.GatewayTokens = [{ token, isMultiUse: false, ccGatewayId: this._precznService.currentCcGatewayId }];
    this.onChange(this.value);
  }

  public tokenizeCardNumIfNecessary = (): void => {
    if (this.useCardConnect) {
      if (this._utilService.isNotNullOrUndefined(this.cardConnectInputComponent)) {
        if (this._utilService.isNotNullOrUndefined(this.value)) {
          if (this._utilService.isNotNullOrUndefined(this.cardConnectInputComponent.token)) {
            this.value.CardNum = this.cardConnectInputComponent.token;
          }
        }
      }
    }
  };

  public onCcNumChange(event: InputEvent) {
    this.cardNumberTouched = true;
    const target = (event.target as HTMLInputElement).value;
    this.value.CardNum = target.replace(/[^0-9]/g, '');
  }

  public onCardNumberBlur(): void {
    this.cardNumberTouched = true;
  }
}
